import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CROPPABLE_IMAGE_MIME_TYPES } from '../components';
import { urlToFile } from '../utils';
import { ImageUploadType, UploadType, useUpload } from './useUpload';

interface CroppedImage {
  id: number;
  url: string;
}

interface CropSourceImage extends CroppedImage {
  originalFilename?: string;
}

export interface OnCropCompleteArgs {
  previousImageId: number;
  image: CroppedImage;
}

interface UseImageCropArgs {
  onCropComplete: (args: OnCropCompleteArgs) => void;
}

interface UseImageCropResult {
  fileToCrop: File | null;
  cropImageId: number | null;
  isCropUploading: boolean;
  openCrop: (image: CropSourceImage) => Promise<void>;
  onCrop: (file: File) => Promise<void>;
  onCropClose: () => void;
}

export const useImageCrop = ({ onCropComplete }: UseImageCropArgs): UseImageCropResult => {
  const { t } = useTranslation();

  const { uploadHandler } = useUpload();

  const [fileToCrop, setFileToCrop] = useState<File | null>(null);

  const [cropImageId, setCropImageId] = useState<number | null>(null);

  const [isCropUploading, setIsCropUploading] = useState<boolean>(false);

  const openCrop = useCallback(
    async ({ id, url, originalFilename }: CropSourceImage) => {
      try {
        const fetchedFile = await urlToFile(url);

        const file = new File([fetchedFile], originalFilename || fetchedFile.name, {
          type: fetchedFile.type,
        });

        if (!CROPPABLE_IMAGE_MIME_TYPES.includes(file.type)) {
          toast.error(t('fileUpload.file-type-error'));

          return;
        }

        setFileToCrop(file);

        setCropImageId(id);
      } catch (_) {
        toast.error(t('fileUpload.error'));
      }
    },
    [t]
  );

  const onCrop = useCallback(
    async (file: File) => {
      if (cropImageId === null) {
        return;
      }

      setFileToCrop(null);

      setIsCropUploading(true);

      const { id, isFileTooLarge } = await uploadHandler({
        file,
        type: UploadType.IMAGE,
        imageUploadType: ImageUploadType.ITEM,
      });

      setIsCropUploading(false);

      setCropImageId(null);

      if (!id) {
        toast.error(t(`fileUpload.${isFileTooLarge ? 'file-too-large-error' : 'error'}`));

        return;
      }

      onCropComplete({
        previousImageId: cropImageId,
        image: { id, url: URL.createObjectURL(file) },
      });
    },
    [cropImageId, onCropComplete, t, uploadHandler]
  );

  const onCropClose = useCallback(() => {
    setFileToCrop(null);
    setCropImageId(null);
  }, []);

  return { fileToCrop, cropImageId, isCropUploading, openCrop, onCrop, onCropClose };
};
